import { useMemo, useState, type ReactNode } from 'react';
import InventoryTypeaheadField, { TypeaheadOption } from './InventoryTypeaheadField';
import { useSearchInventoryRoNumbers } from '../../hooks/inventory/useSearchInventoryRoNumbers';

interface RoNumberFieldProps {
    value: string;
    onChange: (value: string) => void;
    isDisabled?: boolean;
    placeholder?: string;
    /** Rich placeholder (mixed sizes) rendered over the empty input. */
    placeholderNode?: ReactNode;
}

const RoNumberField = ({
    value,
    onChange,
    isDisabled = false,
    placeholder = 'Optional RO #',
    placeholderNode,
}: RoNumberFieldProps) => {
    const [search, setSearch] = useState('');
    const {
        data,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useSearchInventoryRoNumbers(search);

    // Browsing (no query) shows a short recency preview; typing searches the
    // full paginated list.
    const isBrowsing = !search.trim();

    const options: TypeaheadOption[] = useMemo(() => {
        const numbers = data?.pages.flatMap((page) => page.roNumbers) ?? [];
        if (isBrowsing) {
            return numbers
                .slice(0, 3)
                .map((ro) => ({ value: ro, label: ro, badge: 'Recent' }));
        }
        return numbers.map((ro) => ({ value: ro, label: ro }));
    }, [data?.pages, isBrowsing]);

    return (
        <InventoryTypeaheadField
            value={value}
            onChange={onChange}
            options={options}
            onSearch={setSearch}
            placeholder={placeholder}
            placeholderNode={placeholderNode}
            isLoading={isLoading || isFetchingNextPage}
            isDisabled={isDisabled}
            hasMore={!isBrowsing && !!hasNextPage}
            onLoadMore={() => {
                if (hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage();
                }
            }}
        />
    );
};

export default RoNumberField;
